import React from 'react';
import { Clock, User, MessageSquare, ArrowRight } from 'lucide-react';

interface ReallocationEntry {
  at: string;
  by: {
    id: string;
    fullName: string;
  };
  fromUser?: {
    id: string;
    fullName: string;
  };
  toUser: {
    id: string;
    fullName: string;
  };
  role: 'preparer' | 'reviewer' | 'submitter';
  comment?: string;
  reason?: string;
}

interface TaskReallocationHistoryProps {
  reallocationHistory?: ReallocationEntry[];
  className?: string;
}

const getRoleDisplayName = (role: string) => {
  switch (role) {
    case 'preparer': return 'Preparer';
    case 'reviewer': return 'Reviewer';
    case 'submitter': return 'Submitter';
    default: return role;
  }
};

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

export const TaskReallocationHistory: React.FC<TaskReallocationHistoryProps> = ({
  reallocationHistory,
  className = ''
}) => {
  if (!reallocationHistory || reallocationHistory.length === 0) {
    return null;
  }

  return (
    <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-gray-600" />
        <h4 className="text-sm font-medium text-gray-900">Reallocation History</h4>
      </div>
      
      <div className="space-y-3">
        {reallocationHistory.map((entry, index) => (
          <div key={index} className="bg-white rounded-md p-3 border border-gray-200">
            {/* Header with timestamp and role */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {formatDateTime(entry.at)}
                </span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {getRoleDisplayName(entry.role)}
                </span>
              </div>
            </div>
            
            {/* Reallocation flow */}
            <div className="flex items-center gap-2 mb-2 text-sm">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Reallocated by:</span>
              <span className="font-medium text-gray-900">{entry.by.fullName}</span>
            </div>
            
            <div className="flex items-center gap-2 mb-3 text-sm">
              <div className="flex items-center gap-2">
                {entry.fromUser && (
                  <>
                    <span className="text-gray-600">From:</span>
                    <span className="font-medium text-gray-900">{entry.fromUser.fullName}</span>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </>
                )}
                <span className="text-gray-600">To:</span>
                <span className="font-medium text-blue-600">{entry.toUser.fullName}</span>
              </div>
            </div>
            
            {/* Comment and reason */}
            {(entry.comment || entry.reason) && (
              <div className="border-t border-gray-100 pt-2">
                {entry.comment && (
                  <div className="mb-2">
                    <div className="flex items-center gap-1 mb-1">
                      <MessageSquare className="w-3 h-3 text-gray-400" />
                      <span className="text-xs font-medium text-gray-600">Comment:</span>
                    </div>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded p-2">
                      {entry.comment}
                    </p>
                  </div>
                )}
                
                {entry.reason && (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <MessageSquare className="w-3 h-3 text-gray-400" />
                      <span className="text-xs font-medium text-gray-600">Reason:</span>
                    </div>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded p-2">
                      {entry.reason}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
